import React, { useCallback, useEffect, useState } from "react";
import { Drawer, Popconfirm, Spin, Table, Tag, message } from "antd";
import dayjs from "dayjs";
import { deploymentNormApis } from "../../../../api/MachineryEquipment";

/**
 * One deployment norm's version timeline, newest first — the History pattern
 * from the equipment-rates drawer, with the matrix embedded per version.
 *
 * Expanding a version shows its full stored matrix READ-ONLY and HOURS-FREE:
 * hours are derived from a quantity, and a history row has none to offer.
 * Only the LATEST version can be removed (undo) — the previous one comes back
 * into force; older history is immutable.
 */

// Stored fractions render as whole-number percents ("0.05" -> "5%") — the
// same display convention as the editor and the Equipment Rate forms.
const fraction = (value: any, fallback: string): string => {
  if (value === null || value === undefined || value === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? `${Math.round(n * 100 * 1e6) / 1e6}%` : fallback;
};

/** The read-only per-version matrix — the template's columns, no hours. */
const lineColumns: any[] = [
  {
    title: "Equipment",
    key: "asset",
    width: 260,
    render: (_: any, line: any) => (
      <div>
        <div>{line.assetName || "—"}</div>
        <div className="text-soft" style={{ fontSize: 11 }}>
          {line.assetCode}
          {line.toolTypeName ? ` · ${line.toolTypeName}` : ""}
        </div>
      </div>
    ),
  },
  {
    title: "NPF %",
    dataIndex: "nonProductionHoursFactor",
    key: "npf",
    width: 80,
    align: "right" as const,
    // Blank factors stay blank — "0 (default)" and "stored 0" differ.
    render: (v: any) => fraction(v, "—"),
  },
  {
    title: "EUF %",
    dataIndex: "effectiveUtilizationFactor",
    key: "euf",
    width: 80,
    align: "right" as const,
    render: (v: any) => fraction(v, "—"),
  },
  {
    title: "AF %",
    dataIndex: "applicationFactor",
    key: "af",
    width: 80,
    align: "right" as const,
    render: (v: any) => fraction(v, "—"),
  },
  {
    title: "Unit",
    key: "unit",
    width: 110,
    render: (_: any, line: any) => (line.uomName ? `${line.uomName}/Hr` : "—"),
  },
  {
    title: "Prod. Norm",
    dataIndex: "prodNorm",
    key: "prodNorm",
    width: 100,
    align: "right" as const,
    render: (v: any) => {
      const n = Number(v);
      if (v === null || v === undefined || v === "" || !Number.isFinite(n))
        return "—";
      return n > 0 ? (
        String(n)
      ) : (
        <span className="text-soft" title="Considered, not deployed">
          0
        </span>
      );
    },
  },
];

const DeploymentNormHistoryDrawer = ({
  open,
  deploymentNormId,
  normCode = "",
  onClose,
  onChanged,
}: {
  open: boolean;
  deploymentNormId: number | null;
  normCode?: string;
  onClose: () => void;
  /** Fired after an undo, so the list can refresh its in-force summaries. */
  onChanged?: () => void;
}) => {
  const [versions, setVersions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!deploymentNormId) return;
    setLoading(true);
    try {
      const res = await deploymentNormApis.getHistory(deploymentNormId);
      setVersions(res?.data?.versions || []);
    } finally {
      setLoading(false);
    }
  }, [deploymentNormId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleUndoLatest = async () => {
    if (!deploymentNormId) return;
    const res = await deploymentNormApis.deleteLatestVersion(deploymentNormId);
    if (res?.status) {
      message.success(res?.data?.message || "Latest version removed.");
      load();
      onChanged?.();
    } else {
      message.error(res?.data?.message || res?.message || "Could not remove.");
    }
  };

  const columns: any[] = [
    {
      title: "Effective From",
      dataIndex: "effectiveFrom",
      key: "effectiveFrom",
      width: 160,
      render: (v: string, _r: any, index: number) => (
        <div>
          <div>{v ? dayjs(v).format("DD MMM YYYY HH:mm") : "—"}</div>
          {index === 0 ? (
            <Tag color="green" style={{ fontSize: 10, lineHeight: "16px" }}>
              Latest
            </Tag>
          ) : null}
        </div>
      ),
    },
    {
      title: "Equipment",
      key: "lines",
      width: 100,
      align: "right" as const,
      render: (_: any, r: any) => (r.lines || []).length,
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      ellipsis: true,
      render: (v: string) => v || "—",
    },
    {
      title: "By",
      key: "by",
      width: 150,
      render: (_: any, r: any) => (
        <div>
          <div>{r.createdByUserName || r.createdByName || "—"}</div>
          {r.createdAt ? (
            <div className="text-soft" style={{ fontSize: 11 }}>
              {dayjs(r.createdAt).format("DD MMM YYYY HH:mm")}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: "",
      key: "undo",
      width: 70,
      // Only the LATEST version can be removed — older history is immutable.
      render: (_: any, _r: any, index: number) =>
        index === 0 ? (
          <Popconfirm
            title="Undo latest version?"
            description="The previous version comes back into force. Older history cannot be deleted."
            onConfirm={handleUndoLatest}
            okText="Remove"
            cancelText="Keep"
          >
            <a href="#undo" onClick={(e) => e.preventDefault()}>
              Undo
            </a>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <Drawer
      title={`Version History — ${normCode || "Equipment Norm"}`}
      open={open}
      onClose={onClose}
      width={820}
    >
      {loading ? (
        <div className="text-center py-5">
          <Spin />
        </div>
      ) : (
        <>
          <div className="text-soft mb-2" style={{ fontSize: 12 }}>
            Each version is the COMPLETE matrix that was in force from its
            effective instant. Expand a row to read it; hours are not shown —
            they are derived from a quantity, which history does not carry.
          </div>
          <Table
            rowKey="deploymentNormVersionId"
            size="small"
            columns={columns}
            dataSource={versions}
            pagination={false}
            expandable={{
              expandedRowRender: (record: any) => (
                <Table
                  rowKey={(line: any) =>
                    line.versionEquipmentId ?? line.assetId
                  }
                  size="small"
                  columns={lineColumns}
                  dataSource={record.lines || []}
                  pagination={false}
                />
              ),
              rowExpandable: (record: any) => (record.lines || []).length > 0,
            }}
          />
        </>
      )}
    </Drawer>
  );
};

export default DeploymentNormHistoryDrawer;
